/**
 * Account Settings → API tokens (#547 PR 5, collapsing what was
 * originally scoped as PR 8).
 *
 * Personal access tokens (PATs) for programmatic access. The
 * underlying implementation is Better Auth's `apiKey` plugin — keys
 * are hashed at rest, plaintext is surfaced ONCE at create time, and
 * scope is enforced via `metadata.workspaceIds` (empty = all
 * workspaces the user can reach, matching cookie-session authority).
 *
 * Behavior pinned:
 *   - Create token → name + expiration + workspace scope → one-time
 *     plaintext reveal with copy affordance.
 *   - List tokens → name + prefix/start + created/expires + scope
 *     summary.
 *   - Revoke token → themed confirm dialog ("Revoke this token?") then
 *     immediate; #1215. The earlier "no confirmation" was a guess that
 *     a one-click revoke would be unobjectionable because the token can
 *     be regenerated. In practice a leaked PAT is a security incident
 *     and the one-click revoke also let an operator misclick the wrong
 *     row in a list of several. The confirm is brief friction with high
 *     downside-mitigation.
 *
 * View state machine (#1199). Earlier the post-create banner composed
 * ON TOP of the form, leaving the form's primary button mounted as a
 * lingering "Generate token" that read like the create hadn't yet
 * happened. The fix gates the surface on an explicit `view`:
 *     list → form → shown → list
 * (The in-flight create — the form's `submitting` flag — is internal
 * to `CreateTokenForm`; it doesn't get its own page-level view because
 * the form is already mounted and disables itself.) `'shown'` is a
 * distinct surface (no form residue, single "Dismiss" primary action).
 * Dismiss drops the plaintext from state and returns to `'list'` with
 * the new row briefly highlighted (~2s) so the customer sees the
 * confirmation without a toast.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Loader2 } from 'lucide-react'
import { Button, Input, Label } from '@/components/ui'
import { TokenReveal } from '@/components/TokenReveal'
import { authClient } from '@/auth/authClient'
import { workspacesAPI, type WorkspaceSummary } from '@/lib/api'
import { toUserMessage } from '@/lib/userMessage'
import { useConfirm } from '@/hooks/useConfirm'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { formatDate } from '@/lib/utils'

interface ApiTokenRow {
  id: string
  name: string | null
  prefix: string | null
  start: string | null
  createdAt: string | Date
  expiresAt: string | Date | null
  metadata: { workspaceIds?: string[] } | null
}

const EXPIRATION_OPTIONS: ReadonlyArray<{ label: string; seconds: number }> = [
  { label: '30 days', seconds: 60 * 60 * 24 * 30 },
  { label: '90 days', seconds: 60 * 60 * 24 * 90 },
  { label: '1 year', seconds: 60 * 60 * 24 * 365 },
  { label: 'Never (10 years)', seconds: 60 * 60 * 24 * 365 * 10 },
]

/** New-row highlight fade duration after Dismiss, in ms. Long enough
 * that the green band is unmistakable as the customer's eye lands on
 * the list; short enough that the row settles into its normal style
 * before the customer interacts with the table. Mockup spec: ~2s. */
const NEW_ROW_HIGHLIGHT_MS = 2000

type View = 'list' | 'form' | 'shown'

export function AccountTokens() {
  const confirm = useConfirm()
  const tz = useUserTimezone()
  const [tokens, setTokens] = useState<ApiTokenRow[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [view, setView] = useState<View>('list')
  const [revokingId, setRevokingId] = useState<string | null>(null)
  const [freshToken, setFreshToken] = useState<string | null>(null)
  // Id of the most-recently-created token, for the post-Dismiss row
  // highlight. Cleared automatically after NEW_ROW_HIGHLIGHT_MS so the
  // row settles into the normal style without further interaction.
  const [newRowId, setNewRowId] = useState<string | null>(null)

  async function reload() {
    try {
      const { data, error } = await authClient.apiKey.list()
      if (error) throw new Error(error.message ?? 'Failed to load tokens')
      const rows = Array.isArray(data)
        ? (data as unknown as ApiTokenRow[])
        : ((data as { apiKeys?: ApiTokenRow[] } | null)?.apiKeys ?? [])
      setTokens(rows)
      setLoadError(null)
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load tokens')
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  async function handleRevoke(row: ApiTokenRow) {
    // #1215: confirmation gate before revoke. The mutation is
    // technically reversible (regenerate a new token) but rotating
    // every consumer of a misclicked token is a meaningful cleanup,
    // and one-click revoke gives no signal that the operator picked
    // the wrong row in a multi-token list.
    const ok = await confirm({
      title: 'Revoke this token?',
      description: (
        <>
          <strong>{row.name || '(unnamed)'}</strong> stops working immediately. Anything
          using it (CI jobs, the eval harness, local scripts) will get 401s until
          you swap it for a fresh token.
        </>
      ),
      confirmLabel: 'Revoke token',
      destructive: true,
    })
    if (!ok) return
    setRevokingId(row.id)
    setActionError(null)
    try {
      // Better Auth surfaces failures via the returned `{ error }`
      // shape, NOT thrown. Without explicit handling the UI would
      // silently reload as if the revoke succeeded — leaving the
      // (still-live) token visible until the next list response
      // catches up, which it wouldn't since the row is still there.
      // PR #563 Copilot review.
      const { error } = await authClient.apiKey.delete({ keyId: row.id })
      if (error) throw new Error(error.message ?? 'Failed to revoke token')
      await reload()
    } catch (err) {
      setActionError(
        toUserMessage(
          err,
          "Couldn't revoke that token. Try again — if the problem continues, refresh.",
        ),
      )
    } finally {
      setRevokingId(null)
    }
  }

  function handleDismiss() {
    // Drop the plaintext from React state on Dismiss so a re-render
    // can't accidentally resurface it (#1199). The `'shown'` state
    // owns the only mount point for `freshToken`, so once we leave
    // it the value is unreachable from the DOM.
    setFreshToken(null)
    setView('list')
    // Capture the id this timer is responsible for clearing, then
    // only clear if it still matches the current `newRowId` on fire.
    // Without this guard, a second create within the 2s window would
    // install its own `newRowId` and the older timer would stomp it,
    // dropping the newer highlight early (Copilot review, PR #1200).
    const idToClear = newRowId
    setTimeout(() => {
      setNewRowId((current) => (current === idToClear ? null : current))
    }, NEW_ROW_HIGHLIGHT_MS)
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <div>
        <h3 className="text-base font-semibold">API tokens</h3>
        <p className="text-sm text-muted-foreground">
          Personal access tokens for the eval harness and any external
          automation. Tokens carry your identity — keep them secret.
          Revoke any token you suspect is leaked.
        </p>
      </div>

      {view === 'shown' && freshToken && (
        <TokenReveal token={freshToken} onDismiss={handleDismiss} label="New API token" />
      )}

      {view === 'list' && (
        <Button variant="outline" onClick={() => setView('form')}>
          Generate token
        </Button>
      )}

      {view === 'form' && (
        <CreateTokenForm
          onCancel={() => setView('list')}
          onCreated={async (plaintext, newId) => {
            setFreshToken(plaintext)
            setNewRowId(newId)
            setView('shown')
            await reload()
          }}
        />
      )}

      {loadError && (
        <div className="space-y-2">
          <p className="text-sm text-destructive" role="alert">
            {loadError}
          </p>
          <Button variant="outline" onClick={reload}>
            Retry
          </Button>
        </div>
      )}
      {actionError && (
        <p className="text-sm text-destructive" role="alert">
          {actionError}
        </p>
      )}
      {/* List + empty-state + loader render in `'list'` AND `'form'`
          — the form sits above the existing token list so the customer
          can see what they already have while filling out the new
          one. Only `'shown'` hides them, giving the post-create
          banner the page's full attention (#1199). */}
      {view !== 'shown' && !loadError && tokens === null && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading…
        </div>
      )}
      {view !== 'shown' && !loadError && tokens !== null && tokens.length === 0 && (
        <p className="text-sm text-muted-foreground">No API tokens yet.</p>
      )}
      {view !== 'shown' && !loadError && tokens !== null && tokens.length > 0 && (
        <ul className="divide-y divide-border border rounded-md">
          {tokens.map((t) => {
            const isNew = newRowId !== null && t.id === newRowId
            return (
              <li
                key={t.id}
                data-testid={isNew ? 'new-token-row' : undefined}
                className={
                  'flex items-center justify-between py-3 px-3 gap-3 transition-colors duration-1000 ' +
                  (isNew ? 'bg-success/15' : '')
                }
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {t.name || '(unnamed)'}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">
                    {(t.prefix ?? '') + (t.start ?? '') + '…'}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Created {formatDate(t.createdAt, tz)}
                    {t.expiresAt && (
                      <> · Expires {formatDate(t.expiresAt, tz)}</>
                    )}
                    {' · '}
                    {t.metadata?.workspaceIds && t.metadata.workspaceIds.length > 0
                      ? `${t.metadata.workspaceIds.length} workspace${t.metadata.workspaceIds.length === 1 ? '' : 's'}`
                      : 'All workspaces'}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRevoke(t)}
                  disabled={revokingId !== null}
                >
                  {revokingId === t.id ? 'Revoking…' : 'Revoke'}
                </Button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function CreateTokenForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void
  onCreated: (plaintext: string, id: string) => Promise<void>
}) {
  const [name, setName] = useState('')
  const [expiresIn, setExpiresIn] = useState<number>(EXPIRATION_OPTIONS[1]!.seconds)
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[] | null>(null)
  const [scopeWorkspaceIds, setScopeWorkspaceIds] = useState<Set<string>>(new Set())
  const [scopeAll, setScopeAll] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    workspacesAPI
      .list()
      .then(setWorkspaces)
      .catch((err) =>
        setError(
          toUserMessage(
            err,
            "Couldn't load the workspace list. Refresh — if the problem continues, check your connection.",
          ),
        ),
      )
  }, [])

  function toggleWorkspace(id: string) {
    setScopeWorkspaceIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!name.trim()) {
      setError('Name is required.')
      return
    }
    if (!scopeAll && scopeWorkspaceIds.size === 0) {
      setError('Pick at least one workspace, or choose "All workspaces".')
      return
    }
    setSubmitting(true)
    try {
      const workspaceIds = scopeAll ? [] : Array.from(scopeWorkspaceIds)
      const { data, error: e } = await authClient.apiKey.create({
        name: name.trim(),
        expiresIn,
        metadata: { workspaceIds },
      })
      if (e) throw new Error(e.message ?? 'Failed to create token')
      const plaintext = (data as { key?: string })?.key
      const id = (data as { id?: string })?.id
      if (!plaintext) throw new Error('Server returned no token value.')
      if (!id) throw new Error('Server returned no token id.')
      await onCreated(plaintext, id)
    } catch (err) {
      setError(
        toUserMessage(
          err,
          "Couldn't create the token. Try again — if the problem continues, check your connection.",
        ),
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4 rounded border border-border p-4"
    >
      <div className="space-y-2">
        <Label htmlFor="token-name">Name</Label>
        <Input
          id="token-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={50}
          placeholder="e.g. eval-harness laptop"
          autoFocus
          disabled={submitting}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="token-expiration">Expiration</Label>
        <select
          id="token-expiration"
          value={expiresIn}
          onChange={(e) => setExpiresIn(Number(e.target.value))}
          disabled={submitting}
          className="w-full rounded border border-input bg-background px-3 py-2 text-sm"
        >
          {EXPIRATION_OPTIONS.map((opt) => (
            <option key={opt.seconds} value={opt.seconds}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-2">
        <Label>Workspace scope</Label>
        <div className="space-y-1.5 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={scopeAll}
              onChange={() => setScopeAll(true)}
              disabled={submitting}
            />
            <span>All of my workspaces</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={!scopeAll}
              onChange={() => setScopeAll(false)}
              disabled={submitting}
            />
            <span>Limit to specific workspaces</span>
          </label>
        </div>
        {!scopeAll && (
          <div className="ml-6 mt-2 space-y-1.5 max-h-48 overflow-y-auto rounded border border-border p-2">
            {workspaces === null ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                Loading workspaces…
              </div>
            ) : workspaces.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No workspaces available.
              </p>
            ) : (
              workspaces.map((w) => (
                <label key={w._id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={scopeWorkspaceIds.has(w._id)}
                    onChange={() => toggleWorkspace(w._id)}
                    disabled={submitting}
                  />
                  <span>{w.name}</span>
                </label>
              ))
            )}
          </div>
        )}
      </div>
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      <div className="flex items-center gap-2">
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Creating…' : 'Create token'}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
